import PropTypes from 'prop-types';
import { Component } from 'react';

import { reportError } from '../../utils/errorReporting';
import styles from './styles/styles.module.css';

// Chrome: "Failed to fetch dynamically imported module"
// Firefox: "error loading dynamically imported module"
// Safari/WebKit: "Importing a module script failed." (короче и без URL,
// в отличие от двух других - подтверждено реальным событием из GlitchTip,
// а не только документацией по Chrome/Firefox)
// Плюс отдельно - "Unable to preload CSS for <файл>": это код самого
// Vite (packages/vite/src/node/plugins/importAnalysisBuild.ts), а не
// браузера. Срабатывает и при устаревшем деплое, и просто при разовом
// сетевом сбое во время загрузки CSS конкретного чанка - лечится тем же
// способом (перезагрузка), причина неважна.
// Типичная ошибка code-splitting SPA (Vite/webpack): пользователь открыл
// страницу, потом произошёл деплой - хеши файлов в новой сборке другие,
// а браузер у пользователя всё ещё ссылается на старые из уже
// загруженного index.html. Переход на страницу с новым lazy-чанком
// пытается подгрузить файл, которого на сервере уже нет.
const CHUNK_LOAD_ERROR_RE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|NetworkError when attempting to fetch resource|Importing a module script failed|Unable to preload CSS for/i;

// sessionStorage живёт в рамках одной вкладки. Если после автоматической
// перезагрузки та же по типу ошибка случится СНОВА в этой же сессии -
// значит дело не в устаревшем index.html, а в чём-то другом, и дальше
// зацикленно перезагружать не нужно (иначе - мигающий экран вместо
// помощи).
const CHUNK_RELOAD_KEY = 'chunkLoadReloadAttempted';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false, alreadyAttempted: false };
  }

  static getDerivedStateFromError(error) {
    const isChunkError = CHUNK_LOAD_ERROR_RE.test(error?.message || '');
    // Читаем ДО того, как componentDidCatch (запустится следом) успеет
    // сам что-то записать - иначе к моменту рендера флаг уже будет
    // "true" даже для самой первой попытки.
    const alreadyAttempted = sessionStorage.getItem(CHUNK_RELOAD_KEY) === 'true';
    return { hasError: true, isChunkError, alreadyAttempted };
  }

  componentDidCatch(error, errorInfo) {
    reportError(error, { componentStack: errorInfo?.componentStack });

    const isChunkError = CHUNK_LOAD_ERROR_RE.test(error?.message || '');
    const alreadyAttempted = sessionStorage.getItem(CHUNK_RELOAD_KEY) === 'true';
    if (isChunkError && !alreadyAttempted) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, 'true');
      window.location.reload();
    }
  }

  render() {
    const { hasError, isChunkError, alreadyAttempted } = this.state;
    const { children } = this.props;

    if (hasError) {
      if (isChunkError && !alreadyAttempted) {
        // Первая попытка - молча перезагружаем страницу (см.
        // componentDidCatch), ничего не показываем, чтобы не мигал экран
        // "Что-то пошло не так" на долю секунды перед самим reload().
        return null;
      }

      return (
        <div className={styles.errorWrapper}>
          <h2 className={styles.errorSubtitle}>Что-то пошло не так</h2>
          <p className={styles.errorText}>
            Произошла непредвиденная ошибка. Попробуйте обновить страницу — если проблема
            повторится, напишите в поддержку.
          </p>
          <button
            type="button"
            className={styles.errorButton}
            onClick={() => window.location.reload()}
          >
            Обновить страницу
          </button>
        </div>
      );
    }

    return children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ErrorBoundary;
